import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  Mail,
  Send,
  Plus,
  Trash2,
  Edit,
  BarChart3,
  Users,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  RefreshCw,
  Calendar,
  Sparkles,
  Eye,
  Copy,
  FlaskConical,
  TrendingUp,
  MousePointer,
  MailOpen,
  AlertCircle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";

// Status badge component
const CampaignStatusBadge = ({ status }) => {
  const statusConfig = {
    draft: { label: "Brouillon", class: "bg-gray-100 text-gray-700", icon: Edit },
    scheduled: { label: "Programmée", class: "bg-blue-100 text-blue-700", icon: Clock },
    sent: { label: "Envoyée", class: "bg-green-100 text-green-700", icon: CheckCircle },
    queued: { label: "En file", class: "bg-yellow-100 text-yellow-700", icon: Clock }
  };
  
  const config = statusConfig[status] || statusConfig.draft;
  const Icon = config.icon;
  
  return (
    <Badge className={`${config.class} flex items-center gap-1`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </Badge>
  );
};

const CampaignsPage = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [lists, setLists] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [brevoStatus, setBrevoStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [activeBrand, setActiveBrand] = useState(null);
  
  // Create campaign modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newCampaign, setNewCampaign] = useState({
    name: "",
    subject: "",
    sender_name: "",
    sender_email: "",
    html_content: "",
    list_ids: [],
    preview_text: "",
    scheduled_at: "",
    ab_testing_enabled: false,
    subject_b: "",
    split_rule: 50
  });
  
  // Stats modal
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignStats, setCampaignStats] = useState(null);

  // Fetch data
  const fetchActiveBrand = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/brands/active`);
      setActiveBrand(response.data);
      // Update sender name with active brand
      setNewCampaign(prev => ({ ...prev, sender_name: response.data.name || "MAAVI" }));
    } catch (error) {
      console.error("Error fetching active brand:", error);
    }
  }, []);

  const fetchBrevoStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/brevo/status`);
      setBrevoStatus(response.data);
    } catch (error) {
      console.error("Error fetching Brevo status:", error);
      setBrevoStatus({ connected: false, error: "Impossible de se connecter" });
    }
  }, []);

  const fetchCampaigns = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/brevo/campaigns`);
      setCampaigns(response.data.campaigns || []);
    } catch (error) {
      console.error("Error fetching campaigns:", error);
    }
  }, []);

  const fetchLists = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/brevo/lists`);
      setLists(response.data.lists || []);
    } catch (error) {
      console.error("Error fetching lists:", error);
    }
  }, []);

  const fetchAnalytics = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/brevo/analytics`);
      setAnalytics(response.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchActiveBrand(),
        fetchBrevoStatus(),
        fetchCampaigns(),
        fetchLists(),
        fetchAnalytics()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchActiveBrand, fetchBrevoStatus, fetchCampaigns, fetchLists, fetchAnalytics]);

  // Create campaign
  const handleCreateCampaign = async () => {
    if (!newCampaign.name || !newCampaign.subject || !newCampaign.sender_email || !newCampaign.html_content) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    
    if (newCampaign.list_ids.length === 0) {
      toast.error("Veuillez sélectionner au moins une liste");
      return;
    }
    
    setCreating(true);
    try {
      await axios.post(`${API}/brevo/campaigns`, newCampaign);
      toast.success("Campagne créée avec succès");
      setShowCreateModal(false);
      setNewCampaign({
        name: "",
        subject: "",
        sender_name: activeBrand?.name || "MAAVI",
        sender_email: "",
        html_content: "",
        list_ids: [],
        preview_text: "",
        scheduled_at: "",
        ab_testing_enabled: false,
        subject_b: "",
        split_rule: 50
      });
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setCreating(false);
    }
  };

  // Send campaign
  const handleSendCampaign = async (campaignId) => {
    if (!window.confirm("Envoyer cette campagne maintenant ?")) return;
    
    try {
      await axios.post(`${API}/brevo/campaigns/${campaignId}/send`);
      toast.success("Campagne envoyée !");
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l&apos;envoi");
    }
  };

  // Delete campaign
  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm("Supprimer cette campagne ?")) return;
    
    try {
      await axios.delete(`${API}/brevo/campaigns/${campaignId}`);
      toast.success("Campagne supprimée");
      fetchCampaigns();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // View stats
  const handleViewStats = async (campaign) => {
    setSelectedCampaign(campaign);
    setShowStatsModal(true);
    
    try {
      const response = await axios.get(`${API}/brevo/campaigns/${campaign.id}/stats`);
      setCampaignStats(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des stats");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="campaigns-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-semibold">Campagnes Email</h2>
          <p className="text-sm text-muted-foreground">
            Créez et gérez vos campagnes emailing avec Brevo
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { fetchCampaigns(); fetchAnalytics(); }}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
          <Button onClick={() => setShowCreateModal(true)} data-testid="create-campaign-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle campagne
          </Button>
        </div>
      </div>

      {/* Brevo Status */}
      {brevoStatus && (
        <Card className={brevoStatus.connected ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              {brevoStatus.connected ? (
                <>
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="font-medium text-green-800">Brevo connecté</p>
                    <p className="text-sm text-green-600">
                      {brevoStatus.company} • {brevoStatus.email}
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <XCircle className="w-5 h-5 text-red-600" />
                  <div>
                    <p className="font-medium text-red-800">Brevo non connecté</p>
                    <p className="text-sm text-red-600">{brevoStatus.error}</p>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Analytics Summary */}
      {analytics?.summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{analytics.summary.total_campaigns}</p>
                  <p className="text-xs text-muted-foreground">Campagnes</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Send className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{analytics.summary.total_sent.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">Emails envoyés</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <MailOpen className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{analytics.summary.total_opens.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">Ouvertures</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <MousePointer className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{analytics.summary.total_clicks.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">Clics</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{analytics.summary.avg_open_rate}%</p>
                  <p className="text-xs text-muted-foreground">Taux ouverture</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Campaigns List */}
      <Tabs defaultValue="all" className="space-y-4">
        <TabsList>
          <TabsTrigger value="all">Toutes</TabsTrigger>
          <TabsTrigger value="draft">Brouillons</TabsTrigger>
          <TabsTrigger value="sent">Envoyées</TabsTrigger>
          <TabsTrigger value="scheduled">Programmées</TabsTrigger>
        </TabsList>
        
        <TabsContent value="all" className="space-y-4">
          {campaigns.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Mail className="w-12 h-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">Aucune campagne</h3>
                <p className="text-sm text-muted-foreground text-center mb-4">
                  Créez votre première campagne email
                </p>
                <Button onClick={() => setShowCreateModal(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Nouvelle campagne
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {campaigns.map((campaign) => (
                <Card key={campaign.id} data-testid={`campaign-card-${campaign.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-medium">{campaign.name}</h3>
                          <CampaignStatusBadge status={campaign.status} />
                          {campaign.abTesting && (
                            <Badge variant="outline" className="text-xs">
                              <FlaskConical className="w-3 h-3 mr-1" />
                              A/B Test
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">
                          Objet: {campaign.subject}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {campaign.recipients?.listIds?.length || 0} liste(s)
                          </span>
                          {campaign.sentDate && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              Envoyée le {new Date(campaign.sentDate).toLocaleDateString("fr-FR")}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        {campaign.status === "draft" && (
                          <Button
                            size="sm"
                            onClick={() => handleSendCampaign(campaign.id)}
                            data-testid={`send-campaign-${campaign.id}`}
                          >
                            <Send className="w-4 h-4 mr-1" />
                            Envoyer
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleViewStats(campaign)}
                        >
                          <BarChart3 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-500 hover:text-red-600"
                          onClick={() => handleDeleteCampaign(campaign.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
        
        {["draft", "sent", "scheduled"].map((status) => (
          <TabsContent key={status} value={status} className="space-y-4">
            {campaigns.filter(c => c.status === status).length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                Aucune campagne dans cette catégorie
              </p>
            ) : (
              campaigns.filter(c => c.status === status).map((campaign) => (
                <Card key={campaign.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium">{campaign.name}</h3>
                        <p className="text-sm text-muted-foreground">{campaign.subject}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="icon" onClick={() => handleViewStats(campaign)}>
                          <BarChart3 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Create Campaign Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nouvelle campagne</DialogTitle>
            <DialogDescription>
              Créez une nouvelle campagne email
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nom de la campagne *</Label>
                <Input
                  placeholder="Ex: Newsletter Juillet"
                  value={newCampaign.name}
                  onChange={(e) => setNewCampaign({...newCampaign, name: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Email expéditeur *</Label>
                <Input
                  type="email"
                  placeholder="contact@example.com"
                  value={newCampaign.sender_email}
                  onChange={(e) => setNewCampaign({...newCampaign, sender_email: e.target.value})}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Objet de l&apos;email *</Label>
              <Input
                placeholder="Ex: Découvrez nos nouveautés !"
                value={newCampaign.subject}
                onChange={(e) => setNewCampaign({...newCampaign, subject: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Pré-header (aperçu)</Label>
              <Input
                placeholder="Texte affiché après l&apos;objet dans la boîte de réception"
                value={newCampaign.preview_text}
                onChange={(e) => setNewCampaign({...newCampaign, preview_text: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Liste(s) de contacts *</Label>
              <Select
                value={newCampaign.list_ids[0]?.toString() || ""}
                onValueChange={(value) => setNewCampaign({...newCampaign, list_ids: [parseInt(value)]})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner une liste" />
                </SelectTrigger>
                <SelectContent>
                  {lists.map((list) => (
                    <SelectItem key={list.id} value={list.id.toString()}>
                      {list.name} ({list.totalSubscribers} contacts)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Contenu HTML *</Label>
              <Textarea
                placeholder="<html>...</html>"
                rows={8}
                value={newCampaign.html_content}
                onChange={(e) => setNewCampaign({...newCampaign, html_content: e.target.value})}
              />
              <p className="text-xs text-muted-foreground">
                Collez votre code HTML ou utilisez un template Brevo
              </p>
            </div>
            
            {/* A/B Testing */}
            <div className="border rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base">A/B Testing</Label>
                  <p className="text-sm text-muted-foreground">
                    Testez deux objets différents
                  </p>
                </div>
                <Switch
                  checked={newCampaign.ab_testing_enabled}
                  onCheckedChange={(checked) => setNewCampaign({...newCampaign, ab_testing_enabled: checked})}
                />
              </div>
              
              {newCampaign.ab_testing_enabled && (
                <div className="space-y-3 pt-2">
                  <div className="space-y-2">
                    <Label>Objet B (variante)</Label>
                    <Input
                      placeholder="Version alternative de l&apos;objet"
                      value={newCampaign.subject_b}
                      onChange={(e) => setNewCampaign({...newCampaign, subject_b: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Répartition: {newCampaign.split_rule}% A / {100 - newCampaign.split_rule}% B</Label>
                    <input
                      type="range"
                      min="10"
                      max="90"
                      value={newCampaign.split_rule}
                      onChange={(e) => setNewCampaign({...newCampaign, split_rule: parseInt(e.target.value)})}
                      className="w-full"
                    />
                  </div>
                </div>
              )}
            </div>
            
            {/* Scheduling */}
            <div className="space-y-2">
              <Label>Programmation (optionnel)</Label>
              <Input
                type="datetime-local"
                value={newCampaign.scheduled_at}
                onChange={(e) => setNewCampaign({...newCampaign, scheduled_at: e.target.value})}
              />
              <p className="text-xs text-muted-foreground">
                Laissez vide pour enregistrer en brouillon
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreateCampaign} disabled={creating}>
              {creating ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              Créer la campagne
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stats Modal */}
      <Dialog open={showStatsModal} onOpenChange={setShowStatsModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Statistiques de campagne</DialogTitle>
            <DialogDescription>
              {selectedCampaign?.name}
            </DialogDescription>
          </DialogHeader>
          
          {campaignStats ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold">{campaignStats.sent}</p>
                    <p className="text-sm text-muted-foreground">Envoyés</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold">{campaignStats.delivered}</p>
                    <p className="text-sm text-muted-foreground">Délivrés</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-green-600">{campaignStats.open_rate}%</p>
                    <p className="text-sm text-muted-foreground">Taux ouverture</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-blue-600">{campaignStats.click_rate}%</p>
                    <p className="text-sm text-muted-foreground">Taux de clic</p>
                  </CardContent>
                </Card>
              </div>
              
              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Ouvertures uniques</span>
                  <span className="font-medium">{campaignStats.opens}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Clics uniques</span>
                  <span className="font-medium">{campaignStats.clicks}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Désabonnements</span>
                  <span className="font-medium">{campaignStats.unsubscribes}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Bounces</span>
                  <span className="font-medium">{campaignStats.bounces}</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CampaignsPage;
